import { ErrorBoundary, Shadow, VStack } from '@genial/components';
import { useBreakpointValue } from '@genial/utils';
import React, { useEffect, useMemo, useState } from 'react';
import { useRecoilState } from 'recoil';

import { getEmptySelections } from '../../../../components/FilterModal/constants';
import { TableHeadItemOrderBy } from '../../../../components/TableHead';
import { useFilterTransformer } from '../../../../hooks/useFilterTransformer';
import { useRecoilQuery } from '../../../../hooks/useRecoilQuery';
import {
  fixedFeeTermsFiltersAtom,
  fixedFeeTermsOrderByAtom,
} from '../../../../store/fixedFee/atom';
import { fixedFeeTermsQuery } from '../../../../store/fixedFee/selector';
import { FixedFeeTerm } from '../../../../store/fixedFee/types';

import { FixedFeeTermsHeader } from './components/FixedFeeTermsHeader';
import { FixedFeeTermsList } from './components/FixedFeeTermsList';

export interface FixedFeeTermsSectionProps {
  hasSomeImpersonation: boolean;
}

export const FixedFeeTermsSection: React.FC<FixedFeeTermsSectionProps> = ({
  hasSomeImpersonation,
}) => {
  const isMobile = useBreakpointValue({ base: true, sm: false });
  const [searchTerm, setSearchTerm] = useState('');
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const [page, setPage] = useState(1);
  const [loadingComplete, setLoadingComplete] = useState(false);
  const [allData, setAllData] = useState<FixedFeeTerm[]>();
  const [{ availableFilters, appliedFilters }, setProductsFiltersAtom] =
    useRecoilState(fixedFeeTermsFiltersAtom);
  const [{ appliedOrderBy }, setOrderByAtom] = useRecoilState(fixedFeeTermsOrderByAtom);
  const { transformFilters } = useFilterTransformer(availableFilters);

  const orderBy: TableHeadItemOrderBy | undefined = appliedOrderBy?.value
    ? { value: appliedOrderBy.value, direction: appliedOrderBy.direction }
    : undefined;

  const filtersToApply = useMemo(
    () =>
      transformFilters(appliedFilters, {
        transformDateFilter: false,
        transformRangeFieldNames: true,
      }),
    [appliedFilters, transformFilters]
  );

  const { data, error, loading, refresher } = useRecoilQuery(
    fixedFeeTermsQuery({ page, filtersToApply, searchTerm, appliedOrderBy }),
    { if: () => !hasSomeImpersonation }
  );

  const hasFilter = Boolean(searchTerm.trim().length || appliedFilters.length);

  const handleEndReached = () => {
    if (
      !data?.meta.pagination.hasNext ||
      !loadingComplete ||
      loading ||
      error ||
      data.meta.pagination.page !== page
    ) {
      return;
    }

    setPage((prev) => prev + 1);
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setPage(1);
  };

  const handleRefresh = () => {
    setShouldRefresh(true);
    setPage(1);
  };

  const handleAutoOrderBy = (value: string) => {
    const isSameCurrent = orderBy?.value === value;
    const direction = !isSameCurrent ? 'asc' : orderBy?.direction === 'asc' ? 'desc' : 'asc';

    setOrderByAtom((prev) => ({
      ...prev,
      appliedOrderBy: { value, direction },
    }));
  };

  useEffect(() => {
    if (!data || data.meta.pagination.page !== page) {
      return;
    }

    if (page === 1) {
      setAllData(data.data);
    }

    if (page > 1) {
      setAllData((prev) => [...(prev || []), ...data.data]);
    }

    setLoadingComplete(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, data]);

  useEffect(() => {
    if (page !== 1) {
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters, appliedOrderBy]);

  useEffect(() => {
    if (loading) {
      setLoadingComplete(false);
    }
  }, [loading]);

  useEffect(() => {
    if (shouldRefresh) {
      setShouldRefresh(false);
      refresher();
    }
  }, [shouldRefresh, refresher]);

  useEffect(() => {
    setProductsFiltersAtom({
      appliedFilters: [],
      selectedFilters: getEmptySelections(availableFilters),
      availableFilters,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const content = (
    <VStack space="stack.xs">
      <FixedFeeTermsHeader
        disabled={hasSomeImpersonation}
        onSearch={handleSearch}
        onClearSearch={handleClearSearch}
        onRefresh={handleRefresh}
      />
      {error ? (
        <ErrorBoundary onRefresh={refresher} />
      ) : (
        <FixedFeeTermsList
          items={allData}
          isLoading={loading}
          hasFilter={hasFilter}
          orderBy={orderBy}
          currentPage={page}
          isLoadingMore={loading && page > 1}
          onEndReached={handleEndReached}
          onOrderBy={handleAutoOrderBy}
          disabled={hasSomeImpersonation}
        />
      )}
    </VStack>
  );

  if (isMobile) {
    return content;
  }

  return (
    <Shadow level={1} borderRadius="sm">
      {content}
    </Shadow>
  );
};
